/**
 * Venue shape renderers for the unified seat map canvas.
 * These draw decorative field/stage elements behind the seating sections.
 *
 * Supported shapes:
 *   - baseball-diamond  (baseball/softball)
 *   - football-field    (American football)
 *   - soccer-pitch      (soccer/football)
 *   - hockey-rink       (ice hockey)
 *   - basketball-court  (NBA / college)
 *   - theater-stage     (concert / theater with curtains)
 *   - stage             (simple rectangle)
 */

// ─── Baseball Diamond ───────────────────────────────────────────────────────

export function drawBaseballDiamond(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const cx = x + width / 2
  const homeY = y + height * 0.85
  const fieldRadius = width * 0.42

  const grassGreen = '#2d6b30'
  const grassLight = '#3a8a3e'
  const dirtBrown = '#c4a46c'
  const dirtDark = '#a08650'
  const lineWhite = 'rgba(255, 255, 255, 0.85)'

  const homeToBase = width * 0.17

  const home = { x: cx, y: homeY }
  const first = { x: cx + homeToBase, y: homeY - homeToBase }
  const second = { x: cx, y: homeY - homeToBase * 2 }
  const third = { x: cx - homeToBase, y: homeY - homeToBase }
  const pitcher = { x: cx, y: homeY - homeToBase * 1.15 }

  ctx.save()

  // Outfield grass
  ctx.beginPath()
  ctx.moveTo(home.x, home.y)
  ctx.arc(home.x, home.y, fieldRadius, -Math.PI * 0.75, -Math.PI * 0.25, false)
  ctx.closePath()
  ctx.fillStyle = grassGreen
  ctx.fill()

  // Infield dirt
  const dirtScale = 1.35
  ctx.beginPath()
  ctx.moveTo(home.x, home.y + 6)
  ctx.lineTo(cx + homeToBase * dirtScale, homeY - homeToBase + (homeToBase * (dirtScale - 1)) * 0.3)
  ctx.lineTo(second.x, second.y - homeToBase * 0.15)
  ctx.lineTo(cx - homeToBase * dirtScale, homeY - homeToBase + (homeToBase * (dirtScale - 1)) * 0.3)
  ctx.closePath()
  ctx.fillStyle = dirtBrown
  ctx.fill()

  // Infield grass
  const innerScale = 0.65
  ctx.beginPath()
  ctx.moveTo(cx, homeY - homeToBase * 0.5)
  ctx.lineTo(cx + homeToBase * innerScale, homeY - homeToBase)
  ctx.lineTo(cx, homeY - homeToBase * 1.5)
  ctx.lineTo(cx - homeToBase * innerScale, homeY - homeToBase)
  ctx.closePath()
  ctx.fillStyle = grassLight
  ctx.fill()

  // Base paths
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(home.x, home.y)
  ctx.lineTo(first.x, first.y)
  ctx.lineTo(second.x, second.y)
  ctx.lineTo(third.x, third.y)
  ctx.closePath()
  ctx.stroke()

  // Foul lines
  ctx.beginPath()
  const foulExtend = fieldRadius * 1.02
  ctx.moveTo(home.x, home.y)
  ctx.lineTo(home.x + foulExtend * Math.cos(-Math.PI * 0.25), home.y + foulExtend * Math.sin(-Math.PI * 0.25))
  ctx.moveTo(home.x, home.y)
  ctx.lineTo(home.x + foulExtend * Math.cos(-Math.PI * 0.75), home.y + foulExtend * Math.sin(-Math.PI * 0.75))
  ctx.stroke()

  // Bases
  const baseSize = Math.max(3, width * 0.007)
  ctx.fillStyle = lineWhite
  for (const base of [first, second, third]) {
    ctx.save()
    ctx.translate(base.x, base.y)
    ctx.rotate(Math.PI / 4)
    ctx.fillRect(-baseSize / 2, -baseSize / 2, baseSize, baseSize)
    ctx.restore()
  }

  // Home plate
  const hps = Math.max(3, width * 0.006)
  ctx.beginPath()
  ctx.moveTo(home.x, home.y + hps)
  ctx.lineTo(home.x + hps, home.y)
  ctx.lineTo(home.x + hps * 0.7, home.y - hps * 0.7)
  ctx.lineTo(home.x - hps * 0.7, home.y - hps * 0.7)
  ctx.lineTo(home.x - hps, home.y)
  ctx.closePath()
  ctx.fillStyle = lineWhite
  ctx.fill()

  // Pitcher's mound
  const moundR = Math.max(4, width * 0.01)
  ctx.beginPath()
  ctx.arc(pitcher.x, pitcher.y, moundR, 0, Math.PI * 2)
  ctx.fillStyle = dirtDark
  ctx.fill()
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 0.8
  ctx.stroke()
  ctx.fillStyle = lineWhite
  ctx.fillRect(pitcher.x - moundR * 0.5, pitcher.y - 0.8, moundR, 1.6)

  // Outfield fence
  ctx.beginPath()
  ctx.arc(home.x, home.y, fieldRadius, -Math.PI * 0.75, -Math.PI * 0.25, false)
  ctx.strokeStyle = '#1a4a1e'
  ctx.lineWidth = 2.5
  ctx.stroke()

  // Warning track
  ctx.beginPath()
  ctx.arc(home.x, home.y, fieldRadius * 0.93, -Math.PI * 0.75, -Math.PI * 0.25, false)
  ctx.strokeStyle = dirtBrown
  ctx.lineWidth = fieldRadius * 0.04
  ctx.globalAlpha = 0.3
  ctx.stroke()
  ctx.globalAlpha = 1

  ctx.restore()
}

// ─── Football Field ─────────────────────────────────────────────────────────

export function drawFootballField(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const pad = 4
  const fx = x + pad
  const fy = y + pad
  const fw = width - pad * 2
  const fh = height - pad * 2
  const centerY = fy + fh / 2

  const grassDark = '#2d6b30'
  const grassLight = '#358438'
  const lineWhite = 'rgba(255, 255, 255, 0.8)'

  ctx.save()

  // Field background
  ctx.fillStyle = grassDark
  ctx.fillRect(fx, fy, fw, fh)

  // Alternating grass stripes (2 endzones + 10 playing stripes)
  const stripeCount = 12
  const stripeW = fw / stripeCount
  for (let i = 0; i < stripeCount; i++) {
    if (i % 2 === 1) {
      ctx.fillStyle = grassLight
      ctx.fillRect(fx + i * stripeW, fy, stripeW, fh)
    }
  }

  // End zones
  ctx.fillStyle = 'rgba(180, 30, 30, 0.3)'
  ctx.fillRect(fx, fy, stripeW, fh)
  ctx.fillRect(fx + fw - stripeW, fy, stripeW, fh)

  // Yard lines
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 1
  for (let i = 0; i <= stripeCount; i++) {
    ctx.beginPath()
    ctx.moveTo(fx + i * stripeW, fy)
    ctx.lineTo(fx + i * stripeW, fy + fh)
    ctx.stroke()
  }

  // Sidelines
  ctx.lineWidth = 2
  ctx.strokeRect(fx, fy, fw, fh)

  // Yard numbers
  const nums = [10, 20, 30, 40, 50, 40, 30, 20, 10]
  const fontSize = Math.max(6, Math.min(fh * 0.08, 14))
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`
  ctx.fillStyle = lineWhite
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.globalAlpha = 0.5
  for (let i = 0; i < nums.length; i++) {
    const nx = fx + (i + 1.5) * stripeW
    ctx.fillText(String(nums[i]), nx, fy + fh * 0.15)
    ctx.fillText(String(nums[i]), nx, fy + fh * 0.85)
  }
  ctx.globalAlpha = 1

  // 50 yard line logo circle
  const logoR = Math.min(fw, fh) * 0.06
  ctx.beginPath()
  ctx.arc(fx + fw / 2, centerY, logoR, 0, Math.PI * 2)
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 1
  ctx.globalAlpha = 0.4
  ctx.stroke()
  ctx.globalAlpha = 1

  // Hash marks
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 0.6
  const hashLen = fh * 0.02
  for (let i = 1; i < stripeCount; i++) {
    const lx = fx + i * stripeW
    ctx.beginPath()
    ctx.moveTo(lx, fy + fh * 0.33 - hashLen)
    ctx.lineTo(lx, fy + fh * 0.33 + hashLen)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(lx, fy + fh * 0.67 - hashLen)
    ctx.lineTo(lx, fy + fh * 0.67 + hashLen)
    ctx.stroke()
  }

  // Goal posts
  ctx.strokeStyle = '#fbbf24'
  ctx.lineWidth = 1.5
  const gpW = fh * 0.12
  for (const gx of [fx, fx + fw]) {
    const dir = gx === fx ? -1 : 1
    ctx.beginPath()
    ctx.moveTo(gx, centerY - gpW)
    ctx.lineTo(gx + dir * 4, centerY - gpW)
    ctx.moveTo(gx, centerY + gpW)
    ctx.lineTo(gx + dir * 4, centerY + gpW)
    ctx.moveTo(gx + dir * 4, centerY - gpW)
    ctx.lineTo(gx + dir * 4, centerY + gpW)
    ctx.stroke()
  }

  ctx.restore()
}

// ─── Soccer Pitch ───────────────────────────────────────────────────────────

export function drawSoccerPitch(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const pad = 4
  const fx = x + pad
  const fy = y + pad
  const fw = width - pad * 2
  const fh = height - pad * 2
  const cx = fx + fw / 2
  const cy = fy + fh / 2

  const grass = '#2d7a32'
  const lineWhite = 'rgba(255, 255, 255, 0.75)'

  ctx.save()

  // Pitch
  ctx.fillStyle = grass
  ctx.fillRect(fx, fy, fw, fh)

  // Grass stripes
  const stripeCount = 10
  const stripeW = fw / stripeCount
  for (let i = 0; i < stripeCount; i++) {
    if (i % 2 === 1) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
      ctx.fillRect(fx + i * stripeW, fy, stripeW, fh)
    }
  }

  // Outline
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 1.5
  ctx.strokeRect(fx, fy, fw, fh)

  // Center line
  ctx.beginPath()
  ctx.moveTo(cx, fy)
  ctx.lineTo(cx, fy + fh)
  ctx.stroke()

  // Center circle
  const circleR = Math.min(fw, fh) * 0.12
  ctx.beginPath()
  ctx.arc(cx, cy, circleR, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cy, 2, 0, Math.PI * 2)
  ctx.fillStyle = lineWhite
  ctx.fill()

  // Penalty areas
  const paW = fw * 0.15
  const paH = fh * 0.4
  const paY = cy - paH / 2
  ctx.strokeRect(fx, paY, paW, paH)
  ctx.strokeRect(fx + fw - paW, paY, paW, paH)

  // Goal areas
  const gaW = fw * 0.06
  const gaH = fh * 0.2
  const gaY = cy - gaH / 2
  ctx.strokeRect(fx, gaY, gaW, gaH)
  ctx.strokeRect(fx + fw - gaW, gaY, gaW, gaH)

  // Penalty spots + arcs
  const penXL = fx + paW * 0.75
  const penXR = fx + fw - paW * 0.75
  ctx.beginPath()
  ctx.arc(penXL, cy, 1.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(penXR, cy, 1.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(penXL, cy, circleR, -0.65, 0.65, false)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(penXR, cy, circleR, Math.PI - 0.65, Math.PI + 0.65, false)
  ctx.stroke()

  // Corner arcs
  const cornerR = Math.min(fw, fh) * 0.025
  for (const [ax, ay, s, e] of [
    [fx, fy, 0, Math.PI / 2],
    [fx + fw, fy, Math.PI / 2, Math.PI],
    [fx + fw, fy + fh, Math.PI, Math.PI * 1.5],
    [fx, fy + fh, Math.PI * 1.5, Math.PI * 2],
  ] as [number, number, number, number][]) {
    ctx.beginPath()
    ctx.arc(ax, ay, cornerR, s, e)
    ctx.stroke()
  }

  // Goals
  const goalH = fh * 0.15
  ctx.fillStyle = 'rgba(255, 255, 255, 0.15)'
  ctx.fillRect(fx - 4, cy - goalH / 2, 4, goalH)
  ctx.fillRect(fx + fw, cy - goalH / 2, 4, goalH)

  ctx.restore()
}

// ─── Hockey Rink ────────────────────────────────────────────────────────────

export function drawHockeyRink(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const pad = 4
  const fx = x + pad
  const fy = y + pad
  const fw = width - pad * 2
  const fh = height - pad * 2
  const cx = fx + fw / 2
  const cy = fy + fh / 2
  const cornerR = Math.min(fw, fh) * 0.2

  const iceWhite = '#e8edf3'
  const lineRed = 'rgba(220, 38, 38, 0.7)'
  const lineBlue = 'rgba(37, 99, 235, 0.6)'

  ctx.save()

  // Ice surface (rounded rectangle)
  ctx.beginPath()
  ctx.moveTo(fx + cornerR, fy)
  ctx.lineTo(fx + fw - cornerR, fy)
  ctx.arcTo(fx + fw, fy, fx + fw, fy + cornerR, cornerR)
  ctx.lineTo(fx + fw, fy + fh - cornerR)
  ctx.arcTo(fx + fw, fy + fh, fx + fw - cornerR, fy + fh, cornerR)
  ctx.lineTo(fx + cornerR, fy + fh)
  ctx.arcTo(fx, fy + fh, fx, fy + fh - cornerR, cornerR)
  ctx.lineTo(fx, fy + cornerR)
  ctx.arcTo(fx, fy, fx + cornerR, fy, cornerR)
  ctx.closePath()
  ctx.fillStyle = iceWhite
  ctx.fill()
  ctx.strokeStyle = '#333'
  ctx.lineWidth = 2
  ctx.stroke()

  // Center red line
  ctx.strokeStyle = lineRed
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(cx, fy)
  ctx.lineTo(cx, fy + fh)
  ctx.stroke()

  // Center circle + dot
  const circleR = Math.min(fw, fh) * 0.1
  ctx.beginPath()
  ctx.arc(cx, cy, circleR, 0, Math.PI * 2)
  ctx.strokeStyle = lineBlue
  ctx.lineWidth = 1.5
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cy, 2, 0, Math.PI * 2)
  ctx.fillStyle = lineBlue
  ctx.fill()

  // Blue lines
  ctx.strokeStyle = lineBlue
  ctx.lineWidth = 2
  for (const bx of [fx + fw * 0.3, fx + fw * 0.7]) {
    ctx.beginPath()
    ctx.moveTo(bx, fy)
    ctx.lineTo(bx, fy + fh)
    ctx.stroke()
  }

  // Face-off circles
  const faceoffR = Math.min(fw, fh) * 0.07
  ctx.strokeStyle = lineRed
  ctx.lineWidth = 1
  for (const fcx of [fx + fw * 0.2, fx + fw * 0.8]) {
    for (const fcy of [fy + fh * 0.3, fy + fh * 0.7]) {
      ctx.beginPath()
      ctx.arc(fcx, fcy, faceoffR, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(fcx, fcy, 2, 0, Math.PI * 2)
      ctx.fillStyle = lineRed
      ctx.fill()
    }
  }

  // Goal creases
  const creaseR = Math.min(fw, fh) * 0.05
  ctx.fillStyle = 'rgba(173, 216, 230, 0.4)'
  ctx.beginPath()
  ctx.arc(fx + fw * 0.08, cy, creaseR, -Math.PI / 2, Math.PI / 2)
  ctx.closePath()
  ctx.fill()
  ctx.beginPath()
  ctx.arc(fx + fw * 0.92, cy, creaseR, Math.PI / 2, -Math.PI / 2)
  ctx.closePath()
  ctx.fill()

  // Goal nets + lines
  const netW = 4
  const netH = fh * 0.1
  ctx.fillStyle = 'rgba(220, 38, 38, 0.3)'
  ctx.fillRect(fx + fw * 0.08 - netW, cy - netH / 2, netW, netH)
  ctx.fillRect(fx + fw * 0.92, cy - netH / 2, netW, netH)

  ctx.strokeStyle = lineRed
  ctx.lineWidth = 1
  for (const gx of [fx + fw * 0.08, fx + fw * 0.92]) {
    ctx.beginPath()
    ctx.moveTo(gx, fy)
    ctx.lineTo(gx, fy + fh)
    ctx.stroke()
  }

  ctx.restore()
}

// ─── Basketball Court ───────────────────────────────────────────────────────

export function drawBasketballCourt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const pad = 4
  const fx = x + pad
  const fy = y + pad
  const fw = width - pad * 2
  const fh = height - pad * 2
  const cx = fx + fw / 2
  const cy = fy + fh / 2

  const courtColor = '#c87f3b'
  const courtLight = '#d4923f'
  const lineWhite = 'rgba(255, 255, 255, 0.8)'

  ctx.save()

  // Court floor with planks
  ctx.fillStyle = courtColor
  ctx.fillRect(fx, fy, fw, fh)
  const plankCount = 20
  const plankW = fw / plankCount
  for (let i = 0; i < plankCount; i++) {
    if (i % 2 === 1) {
      ctx.fillStyle = courtLight
      ctx.fillRect(fx + i * plankW, fy, plankW, fh)
    }
  }

  // Outline + center line
  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 2
  ctx.strokeRect(fx, fy, fw, fh)
  ctx.beginPath()
  ctx.moveTo(cx, fy)
  ctx.lineTo(cx, fy + fh)
  ctx.stroke()

  // Center circle
  const circleR = Math.min(fw, fh) * 0.1
  ctx.beginPath()
  ctx.arc(cx, cy, circleR, 0, Math.PI * 2)
  ctx.stroke()

  // Three-point arcs
  const threeR = Math.min(fw, fh) * 0.27
  const bbX1 = fx + fw * 0.05
  const bbX2 = fx + fw * 0.95

  const cosVal = Math.min(1, (fw * 0.12) / threeR)
  const threeAngle = Math.acos(cosVal)

  ctx.beginPath()
  ctx.moveTo(bbX1, fy + fh * 0.07)
  ctx.lineTo(bbX1 + fw * 0.12, fy + fh * 0.07)
  ctx.arc(bbX1, cy, threeR, -threeAngle, threeAngle, false)
  ctx.lineTo(bbX1, fy + fh * 0.93)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(bbX2, fy + fh * 0.07)
  ctx.lineTo(bbX2 - fw * 0.12, fy + fh * 0.07)
  ctx.arc(bbX2, cy, threeR, Math.PI - threeAngle, Math.PI + threeAngle, false)
  ctx.lineTo(bbX2, fy + fh * 0.93)
  ctx.stroke()

  // Key/paint areas
  const keyW = fw * 0.15
  const keyH = fh * 0.35
  const keyY = cy - keyH / 2
  ctx.strokeRect(fx, keyY, keyW, keyH)
  ctx.strokeRect(fx + fw - keyW, keyY, keyW, keyH)

  // Free throw circles
  const ftR = keyH / 2
  ctx.beginPath()
  ctx.arc(fx + keyW, cy, ftR, -Math.PI / 2, Math.PI / 2)
  ctx.stroke()
  ctx.setLineDash([3, 3])
  ctx.beginPath()
  ctx.arc(fx + keyW, cy, ftR, Math.PI / 2, -Math.PI / 2)
  ctx.stroke()
  ctx.setLineDash([])
  ctx.beginPath()
  ctx.arc(fx + fw - keyW, cy, ftR, Math.PI / 2, -Math.PI / 2)
  ctx.stroke()
  ctx.setLineDash([3, 3])
  ctx.beginPath()
  ctx.arc(fx + fw - keyW, cy, ftR, -Math.PI / 2, Math.PI / 2)
  ctx.stroke()
  ctx.setLineDash([])

  // Rims + backboards
  const rimR = Math.min(fw, fh) * 0.015
  ctx.lineWidth = 2
  ctx.strokeStyle = '#ff6600'
  ctx.beginPath()
  ctx.arc(bbX1 + 3, cy, rimR, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(bbX2 - 3, cy, rimR, 0, Math.PI * 2)
  ctx.stroke()

  ctx.strokeStyle = lineWhite
  ctx.lineWidth = 2
  const bbH = fh * 0.08
  ctx.beginPath()
  ctx.moveTo(bbX1, cy - bbH)
  ctx.lineTo(bbX1, cy + bbH)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(bbX2, cy - bbH)
  ctx.lineTo(bbX2, cy + bbH)
  ctx.stroke()

  ctx.restore()
}

// ─── Theater / Concert Stage ────────────────────────────────────────────────

export function drawTheaterStage(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const cx = x + width / 2
  const stageW = width * 0.7
  const stageH = height * 0.4
  const stageX = cx - stageW / 2
  const stageY = y + height * 0.1
  const r = 8

  ctx.save()

  // Stage platform
  ctx.beginPath()
  ctx.moveTo(stageX + r, stageY)
  ctx.lineTo(stageX + stageW - r, stageY)
  ctx.arcTo(stageX + stageW, stageY, stageX + stageW, stageY + r, r)
  ctx.lineTo(stageX + stageW, stageY + stageH)
  ctx.lineTo(stageX, stageY + stageH)
  ctx.lineTo(stageX, stageY + r)
  ctx.arcTo(stageX, stageY, stageX + r, stageY, r)
  ctx.closePath()
  ctx.fillStyle = '#1e293b'
  ctx.fill()

  // Stage edge highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)'
  ctx.fillRect(stageX, stageY + stageH - 4, stageW, 4)

  // Stage label
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
  const fontSize = Math.min(stageW / 8, 16)
  ctx.font = `bold ${fontSize}px Inter, system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('STAGE', cx, stageY + stageH / 2)

  // Curtain wings
  const wingW = width * 0.08
  ctx.fillStyle = 'rgba(127, 29, 29, 0.3)'
  ctx.beginPath()
  ctx.moveTo(stageX - wingW * 0.3, stageY - 6)
  ctx.lineTo(stageX + 4, stageY - 6)
  ctx.lineTo(stageX + 4, stageY + stageH + 10)
  ctx.lineTo(stageX - wingW * 0.3, stageY + stageH + 15)
  ctx.closePath()
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(stageX + stageW + wingW * 0.3, stageY - 6)
  ctx.lineTo(stageX + stageW - 4, stageY - 6)
  ctx.lineTo(stageX + stageW - 4, stageY + stageH + 10)
  ctx.lineTo(stageX + stageW + wingW * 0.3, stageY + stageH + 15)
  ctx.closePath()
  ctx.fill()

  // Stage lights
  const lightCount = 7
  const lightR = Math.max(2, stageW * 0.005)
  ctx.fillStyle = 'rgba(250, 204, 21, 0.4)'
  for (let i = 0; i < lightCount; i++) {
    const lx = stageX + (i + 0.5) * (stageW / lightCount)
    ctx.beginPath()
    ctx.arc(lx, stageY + stageH + 3, lightR, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

// ─── Boxing Ring ───────────────────────────────────────────────────────────

export interface BoxingRingConfig {
  apronColor?: string       // Outer skirt/apron (default: dark navy)
  matColor?: string         // Canvas mat (default: cream)
  ropeColor?: string        // Primary rope color (default: red)
  ropeAccentColor?: string  // Secondary rope color (default: white)
  postColor?: string        // Corner post/turnbuckle color
  centerLogoColor?: string  // Center circle color
  label?: string            // Center text (default: none)
  labelColor?: string       // Center text color
  labelFont?: string        // Font family for label
  showSpotlight?: boolean   // Glow effect behind ring
}

export function drawBoxingRing(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, width: number, height: number,
  config?: BoxingRingConfig
) {
  const apronColor = config?.apronColor || '#111827'
  const matColor = config?.matColor || '#e8dcc8'
  const ropeColor = config?.ropeColor || '#dc2626'
  const ropeAccent = config?.ropeAccentColor || '#f5f5f4'
  const postColor = config?.postColor || '#78716c'
  const centerColor = config?.centerLogoColor || ropeColor
  const label = config?.label || ''
  const labelColor = config?.labelColor || 'rgba(30,30,30,0.6)'
  const labelFont = config?.labelFont || 'Inter, system-ui, sans-serif'
  const showSpotlight = config?.showSpotlight !== false

  const cx = x + width / 2
  const cy = y + height / 2
  const ringSize = Math.min(width, height)

  ctx.save()

  // Spotlight glow
  if (showSpotlight) {
    const glowR = ringSize * 0.55
    const glow = ctx.createRadialGradient(cx, cy, ringSize * 0.05, cx, cy, glowR)
    glow.addColorStop(0, 'rgba(255, 255, 220, 0.10)')
    glow.addColorStop(0.5, 'rgba(255, 255, 220, 0.03)')
    glow.addColorStop(1, 'rgba(255, 255, 220, 0)')
    ctx.fillStyle = glow
    ctx.beginPath()
    ctx.arc(cx, cy, glowR, 0, Math.PI * 2)
    ctx.fill()
  }

  // Outer apron/platform — elevated look with shadow
  const apronPad = ringSize * 0.04
  const apronX = cx - ringSize / 2 + apronPad
  const apronY = cy - ringSize / 2 + apronPad
  const apronW = ringSize - apronPad * 2
  const apronH = ringSize - apronPad * 2
  const apronR = ringSize * 0.025

  // Platform shadow
  ctx.shadowColor = 'rgba(0,0,0,0.4)'
  ctx.shadowBlur = ringSize * 0.04
  ctx.shadowOffsetY = ringSize * 0.015

  // Apron with gradient (slightly 3D)
  const apronGrad = ctx.createLinearGradient(apronX, apronY, apronX, apronY + apronH)
  apronGrad.addColorStop(0, lightenHex(apronColor, 15))
  apronGrad.addColorStop(0.5, apronColor)
  apronGrad.addColorStop(1, darkenHex(apronColor, 10))
  ctx.fillStyle = apronGrad
  ctx.beginPath()
  ctx.roundRect(apronX, apronY, apronW, apronH, apronR)
  ctx.fill()
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0

  // Apron edge highlight
  ctx.strokeStyle = lightenHex(apronColor, 25)
  ctx.lineWidth = Math.max(0.5, ringSize * 0.003)
  ctx.beginPath()
  ctx.moveTo(apronX + apronR, apronY)
  ctx.lineTo(apronX + apronW - apronR, apronY)
  ctx.stroke()

  // Ring canvas mat with subtle texture gradient
  const matPad = ringSize * 0.11
  const matX = cx - ringSize / 2 + matPad
  const matY = cy - ringSize / 2 + matPad
  const matW = ringSize - matPad * 2
  const matH = ringSize - matPad * 2
  const matR = ringSize * 0.015

  const matGrad = ctx.createRadialGradient(cx, cy - matH * 0.15, matW * 0.1, cx, cy, matW * 0.6)
  matGrad.addColorStop(0, lightenHex(matColor, 8))
  matGrad.addColorStop(0.7, matColor)
  matGrad.addColorStop(1, darkenHex(matColor, 12))
  ctx.fillStyle = matGrad
  ctx.beginPath()
  ctx.roundRect(matX, matY, matW, matH, matR)
  ctx.fill()

  // Mat border (subtle)
  ctx.strokeStyle = darkenHex(matColor, 20)
  ctx.lineWidth = Math.max(0.5, ringSize * 0.004)
  ctx.stroke()

  // Center circle + ring design
  const logoR = matW * 0.2
  // Outer circle
  ctx.beginPath()
  ctx.arc(cx, cy, logoR, 0, Math.PI * 2)
  ctx.fillStyle = hexToRgba(centerColor, 0.08)
  ctx.fill()
  ctx.strokeStyle = hexToRgba(centerColor, 0.3)
  ctx.lineWidth = Math.max(1.5, ringSize * 0.006)
  ctx.stroke()
  // Inner circle
  ctx.beginPath()
  ctx.arc(cx, cy, logoR * 0.6, 0, Math.PI * 2)
  ctx.strokeStyle = hexToRgba(centerColor, 0.15)
  ctx.lineWidth = Math.max(0.8, ringSize * 0.003)
  ctx.stroke()

  // Center label
  if (label) {
    const fontSize = Math.max(8, Math.min(logoR * 0.7, 28))
    ctx.fillStyle = labelColor
    ctx.font = `800 ${fontSize}px ${labelFont}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(label, cx, cy)
  }

  // Ropes — 3 ropes with proper spacing from posts
  const ropeInset = ringSize * 0.085
  const rX = cx - ringSize / 2 + ropeInset
  const rY = cy - ringSize / 2 + ropeInset
  const rW = ringSize - ropeInset * 2
  const rH = ringSize - ropeInset * 2
  const ropeThickness = Math.max(1.2, ringSize * 0.007)
  const ropeGap = ropeThickness * 3.5
  const ropeColors = [ropeColor, ropeAccent, ropeColor]

  for (let i = 0; i < 3; i++) {
    const offset = (i - 1) * ropeGap
    ctx.strokeStyle = ropeColors[i]
    ctx.lineWidth = ropeThickness
    ctx.globalAlpha = i === 1 ? 0.85 : 0.75
    ctx.lineCap = 'round'

    // Top rope
    ctx.beginPath()
    ctx.moveTo(rX, rY + offset)
    ctx.lineTo(rX + rW, rY + offset)
    ctx.stroke()
    // Bottom rope
    ctx.beginPath()
    ctx.moveTo(rX, rY + rH - offset)
    ctx.lineTo(rX + rW, rY + rH - offset)
    ctx.stroke()
    // Left rope
    ctx.beginPath()
    ctx.moveTo(rX + offset, rY)
    ctx.lineTo(rX + offset, rY + rH)
    ctx.stroke()
    // Right rope
    ctx.beginPath()
    ctx.moveTo(rX + rW - offset, rY)
    ctx.lineTo(rX + rW - offset, rY + rH)
    ctx.stroke()
  }
  ctx.globalAlpha = 1
  ctx.lineCap = 'butt'

  // Corner posts/turnbuckles — metallic look
  const postR = Math.max(3, ringSize * 0.022)
  const posts: [number, number][] = [
    [rX, rY], [rX + rW, rY], [rX + rW, rY + rH], [rX, rY + rH]
  ]
  for (const [px, py] of posts) {
    // Post shadow
    ctx.beginPath()
    ctx.arc(px, py + postR * 0.3, postR * 1.2, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(0,0,0,0.25)'
    ctx.fill()
    // Post base (dark)
    ctx.beginPath()
    ctx.arc(px, py, postR * 1.3, 0, Math.PI * 2)
    ctx.fillStyle = darkenHex(postColor, 30)
    ctx.fill()
    // Turnbuckle (metallic gradient)
    const postGrad = ctx.createRadialGradient(px - postR * 0.3, py - postR * 0.3, 0, px, py, postR)
    postGrad.addColorStop(0, lightenHex(postColor, 40))
    postGrad.addColorStop(0.5, postColor)
    postGrad.addColorStop(1, darkenHex(postColor, 20))
    ctx.beginPath()
    ctx.arc(px, py, postR, 0, Math.PI * 2)
    ctx.fillStyle = postGrad
    ctx.fill()
    ctx.strokeStyle = darkenHex(postColor, 15)
    ctx.lineWidth = Math.max(0.5, ringSize * 0.003)
    ctx.stroke()
    // Highlight dot
    ctx.beginPath()
    ctx.arc(px - postR * 0.25, py - postR * 0.25, postR * 0.3, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255,255,255,0.35)'
    ctx.fill()
  }

  ctx.restore()
}

// Hex color utilities for boxing ring
function lightenHex(hex: string, amount: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return hex
  const r = Math.min(255, parseInt(hex.slice(1,3),16) + amount)
  const g = Math.min(255, parseInt(hex.slice(3,5),16) + amount)
  const b = Math.min(255, parseInt(hex.slice(5,7),16) + amount)
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`
}
function darkenHex(hex: string, amount: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return hex
  const r = Math.max(0, parseInt(hex.slice(1,3),16) - amount)
  const g = Math.max(0, parseInt(hex.slice(3,5),16) - amount)
  const b = Math.max(0, parseInt(hex.slice(5,7),16) - amount)
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`
}
function hexToRgba(hex: string, alpha: number): string {
  if (!hex.startsWith('#') || hex.length < 7) return hex
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16)
  return `rgba(${r},${g},${b},${alpha})`
}

// ─── Simple Stage ───────────────────────────────────────────────────────────

function drawSimpleStage(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const r = 6
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.arcTo(x + width, y, x + width, y + r, r)
  ctx.lineTo(x + width, y + height - r)
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r)
  ctx.lineTo(x + r, y + height)
  ctx.arcTo(x, y + height, x, y + height - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
  ctx.fillStyle = '#1e293b'
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 14px Inter, system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('STAGE', x + width / 2, y + height / 2)
}

// ─── Dispatcher ─────────────────────────────────────────────────────────────

export function drawVenueShape(
  ctx: CanvasRenderingContext2D,
  shape: { type: string; x: number; y: number; width: number; height: number }
) {
  switch (shape.type) {
    case 'baseball-diamond':
      drawBaseballDiamond(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'football-field':
      drawFootballField(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'soccer-pitch':
      drawSoccerPitch(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'hockey-rink':
      drawHockeyRink(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'basketball-court':
      drawBasketballCourt(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'theater-stage':
      drawTheaterStage(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'boxing-ring':
      drawBoxingRing(ctx, shape.x, shape.y, shape.width, shape.height)
      break
    case 'stage':
      drawSimpleStage(ctx, shape.x, shape.y, shape.width, shape.height)
      break
  }
}
